import React, { useEffect, useState, useRef } from "react";
import Loader from "pages/loader/Loader";
import { call } from "services/axiosService/core";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import * as Sentry from "@sentry/browser";
import { useMaterialUIController } from "context";
import PaymentSetupWrapper from "./PaymentSetupWrapper";
import MDTypography from "components/MDTypography";
import { Card, CardContent, IconButton, Typography, Box, Chip } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CircularProgress from "@mui/material/CircularProgress";

const ManagePaymentMethods = ({
  handleCloseManagePaymentMethodModal,
  setOpenSnackbar,
  setSeverity,
  setAlertMessage,
  fromOnboarding = false,
  t,
}) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentId, setDefaultPaymentId] = useState(null);
  const [step, setStep] = useState(1);
  const [deleting, setDeleting] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [controller, dispatch] = useMaterialUIController();
  const [openAddPaymentMethodModal, setOpenAddPaymentMethodModal] = useState(false);
  const { profile, persona } = controller;

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const isCardExpired = (expMonth: number, expYear: number) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    return expYear < currentYear || (expYear === currentYear && expMonth < currentMonth);
  };

  //   const getCardBackground = (brand: string) => {
  //     switch (brand.toLowerCase()) {
  //       case "visa":
  //         return "linear-gradient(135deg, #1a1f71, #3a7bd5)";
  //       case "mastercard":
  //         return "linear-gradient(135deg, #ff5f6d, #ffc371)";
  //       case "amex":
  //         return "linear-gradient(135deg, #4facfe, #00f2fe)";
  //       default:
  //         return "linear-gradient(135deg, #434343, #000000)";
  //     }
  //   };

  // const CardBrandBadge = ({ brand }) => {
  //   return (
  //     <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-700 border border-gray-300">
  //       {brand.toUpperCase()}
  //     </span>
  //   );
  // };

  const loadPaymentMethods = async () => {
    setStep(1);
    try {
      const response = await call(`payments/get-payment-methods/${profile.id}`, "GET", {});
      if (response.status === 200) {
        console.log("response is", response);
        const methods = response?.data?.payment_methods || [];
        const defaultId = response?.data?.default_payment_id || null;
        if (methods.length === 0) {
          setPaymentMethods([]);
        } else {
          let sortedMethods = methods;

          if (defaultId) {
            // Move default method to the front
            sortedMethods = [
              ...methods.filter((pm) => pm.id === defaultId),
              ...methods.filter((pm) => pm.id !== defaultId),
            ];
          }

          setPaymentMethods(sortedMethods);
          setDefaultPaymentId(defaultId);
        }
        setStep(2);
      } else {
        setOpenSnackbar(true);
        setSeverity("error");
        setAlertMessage("Error while loading payment methods");
      }
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };

  const handleOpenAddPaymentMethodModal = () => {
    console.log("open add payment method modal");
    setOpenAddPaymentMethodModal(true);
  };

  const handleCloseAddPaymentMethodModal = () => {
    loadPaymentMethods();
    setOpenAddPaymentMethodModal(false);
  };

  const openDeletePaymentModel = (id) => {
    setPaymentId(id);
    setStep(3);
    console.log("delete payment method", id);
  };

  const handleDeletePaymentMethod = () => {
    try {
      setDeleting(true);
      call(`payments/delete-payment-method/${paymentId}/${profile.id}`, "DELETE", {}).then(
        (response) => {
          if (response.status === 200) {
            setPaymentId(null);
            loadPaymentMethods();
            setStep(2);
            setOpenSnackbar(true);
            setSeverity("success");
            setAlertMessage("Payment method deleted successfully");
            setDeleting(false);
          } else {
            setStep(2);
            setOpenSnackbar(true);
            setDeleting(false);
            setSeverity("error");
            setAlertMessage("Error while deleting payment method");
          }
        }
      );
    } catch (error) {
      Sentry.captureException(error);
      console.log(error);
    }
  };

  const handleSetDefaultPaymentMethod = async (id) => {
    try {
      const body = {
        user_id: profile.id,
        payment_id: id,
      };
      const response = await call("payments/set-default-payment-method", "PUT", { body: body });
      if (response.status === 200) {
        // setDefaultPaymentId(id); // update UI
        setOpenSnackbar(true);
        loadPaymentMethods();
        setSeverity("success");
        setAlertMessage("Default payment method updated successfully");
      } else {
        setOpenSnackbar(true);
        setSeverity("error");
        setAlertMessage("Failed to update default payment method");
      }
    } catch (error) {
      console.error(error);
      setOpenSnackbar(true);
      setSeverity("error");
      setAlertMessage("Error occurred while setting default payment method");
    }
  };

  const handleCloseDeletePaymentMethodModal = () => {
    setPaymentId(null);
    setStep(2);
  };

  return (
    <>
      {!openAddPaymentMethodModal && (
        <Dialog
          open={true}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: {
                xs: "90vw",
                sm: "80vw",
                md: "60vw",
                lg: "40vw",
              },
              maxWidth: "1000px",
              borderRadius: "16px",
            },
          }}
        >
          {/* {!fromOnboarding && (
            <CloseIcon
              fontSize="medium"
              onClick={handleCloseManagePaymentMethodModal}
              style={{ color: "red", display: "block", marginLeft: "auto", cursor: "pointer" }}
            />
          )} */}

          <DialogTitle>
            <MDTypography variant="h4" textAlign="center" mb={1}>
              {t("headings.managePaymentMethods")}
            </MDTypography>
          </DialogTitle>

          {/* show the loading text */}
          {step === 1 && (
            <DialogContent
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "300px",
                overflow: "hidden",
                padding: 4,
              }}
            >
              <Loader loaderText={t("loaderText.loadingPaymentMethods")} />
            </DialogContent>
          )}
          {/* show the list of payment metods */}

          {step == 2 && (
            <DialogContent
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minHeight: "300px",
                padding: 4,
              }}
            >
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                <Button
                  variant="outlined"
                  sx={{
                    color: "#10B0D5",
                    borderColor: "#10B0D5",
                    "&:hover": { backgroundColor: "#e8f7fb", borderColor: "#089ebe" },
                    textTransform: "none",
                    fontSize: "14px",
                    borderRadius: "12px",
                    px: 3,
                    py: 1.5,
                  }}
                  onClick={() => setStep(4)}
                >
                  {t("buttonTitles.doItLater") || "Do it later"}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#f6821e",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#eb6f05" },
                    textTransform: "none",
                    fontSize: "14px",
                    borderRadius: "12px",
                    px: 3,
                    py: 1.5,
                  }}
                  onClick={() => handleOpenAddPaymentMethodModal()}
                >
                  {t("buttonTitles.addPaymentMethod")}
                </Button>
              </div>
              {paymentMethods.length == 0 ? (
                <div
                  style={{
                    border: "1px dashed #b0bec5",
                    borderRadius: "12px",
                    padding: "32px 24px",
                    textAlign: "center",
                    background: "#fafafa",
                    marginTop: "24px",
                  }}
                >
                  <MDTypography variant="h6" color="textSecondary" gutterBottom>
                    {t("alerts.addPayment.noPaymentMethodTitle") || "No Payment Methods Found"}
                  </MDTypography>
                  <MDTypography variant="body2" color="textPrimary">
                    {t("alerts.addPayment.noPaymentMethod") ||
                      "You have not added any payment methods yet. Click the button above to add one."}
                  </MDTypography>
                </div>
              ) : (
                <Box display="grid" justifyContent="center" gap={2}>
                  {paymentMethods.map((pm) => {
                    const card = pm.card;
                    const brand = pm.card.brand;
                    const expired = isCardExpired(card.exp_month, card.exp_year);

                    return (
                      <Card
                        key={pm.id}
                        variant="outlined"
                        sx={{
                          width: {
                            xs: "90vw",
                            sm: "80vw",
                            md: "500px",
                          },
                          maxWidth: "500px",
                          height: "150px",
                          borderRadius: "16px",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                          background: "linear-gradient(135deg, #1a1f71, #3a7bd5)",
                          color: "white",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                          p: 3,
                          mx: "auto", // center horizontally
                        }}
                      >
                        <Box display="flex" justifyContent="space-between">
                          <Box
                            sx={{
                              backgroundColor: "rgba(255, 255, 255, 0.2)",
                              px: 2,
                              py: 0.5,
                              borderRadius: "8px",
                              fontSize: "0.9rem",
                              fontWeight: "bold",
                              textTransform: "uppercase",
                              color: "gold",
                            }}
                          >
                            {pm.card.brand}
                          </Box>

                          {defaultPaymentId === pm.id && (
                            <Chip
                              label="DEFAULT"
                              size="medium"
                              sx={{
                                backgroundColor: "gold",
                                color: "black",
                                fontWeight: "bold",
                                fontSize: "0.9rem",
                                letterSpacing: "0.5px",
                                borderRadius: "6px",
                                height: "30px",
                                marginLeft: "8px",
                                "& .MuiChip-label": {
                                  px: 1.5,
                                  py: 0.2,
                                },
                              }}
                            />
                          )}

                          {defaultPaymentId !== pm.id && (
                            <Chip
                              label="Set as Default"
                              onClick={() => handleSetDefaultPaymentMethod(pm.id)}
                              clickable
                              size="medium"
                              sx={{
                                backgroundColor: "#FFD700", // gold
                                color: "#000",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                textTransform: "uppercase",
                                borderRadius: "16px",
                                height: "28px",
                                marginLeft: "10px",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                  backgroundColor: "#FFC300", // darker gold
                                },
                                "& .MuiChip-label": {
                                  px: 2,
                                },
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="h6" sx={{ letterSpacing: 2, color: "gold" }}>
                          •••• •••• •••• {pm.card.last4}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="h6" color="gold">
                              Expiry
                            </Typography>
                            <Typography variant="body1" color="gold">
                              {pm.card.exp_month}/{pm.card.exp_year}
                            </Typography>
                          </Box>
                          <IconButton
                            color="error"
                            onClick={() => openDeletePaymentModel(pm.id)}
                            sx={{
                              backgroundColor: "rgba(255,255,255,0.15)",
                              "&:hover": { backgroundColor: "rgba(255,0,0,0.3)" },
                            }}
                          >
                            <DeleteIcon sx={{ color: "white" }} />
                          </IconButton>
                        </Box>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </DialogContent>
          )}

          {/* do it later - 14 days reminder */}

          {step === 4 && (
            <DialogContent
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "200px",
                padding: 4,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#e8f7fb",
                  border: "1px solid #10B0D5",
                  borderRadius: "12px",
                  padding: "24px",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                <MDTypography variant="h5" mb={1} sx={{ color: "#10B0D5" }}>
                  {t("alerts.addPayment.doItLaterTitle") || "No problem!"}
                </MDTypography>
                <MDTypography variant="body1" sx={{ color: "#444", lineHeight: 1.7 }}>
                  {t("alerts.addPayment.doItLaterMessage") ||
                    "You have 14 days to add a payment method. You can do this anytime from your profile under Manage Payment Methods."}
                </MDTypography>
              </Box>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#10B0D5",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#089ebe" },
                  textTransform: "none",
                  fontSize: "14px",
                  borderRadius: "12px",
                  px: 4,
                  py: 1.5,
                  mt: 1,
                }}
                onClick={handleCloseManagePaymentMethodModal}
              >
                {t("buttonTitles.close") || "Close"}
              </Button>
            </DialogContent>
          )}

          {/* delete payment metod */}

          {step == 3 && (
            <>
              <DialogContent>
                <MDTypography variant="h4" color="textSecondary" gutterBottom>
                  {t("alerts.addPayment.deletePaymentMethod")}
                </MDTypography>
                <MDTypography variant="body2">
                  {t("alerts.addPayment.deletePaymentMethodMessage")}
                </MDTypography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => handleDeletePaymentMethod()}
                  style={{
                    marginTop: "15px",
                    color: "white",
                    background: "#10B0D5",
                  }}
                >
                  {deleting ? (
                    <CircularProgress color="white" size={20} />
                  ) : (
                    t("buttonTitles.delete")
                  )}
                </Button>
                <Button
                  onClick={() => handleCloseDeletePaymentMethodModal()}
                  style={{
                    marginTop: "15px",
                    color: "white",
                    background: "#10B0D5",
                  }}
                >
                  {t("buttonTitles.cancel")}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      )}
      {openAddPaymentMethodModal && (
        <PaymentSetupWrapper
          openAddPaymentMethodModal={openAddPaymentMethodModal}
          handleCloseAddPaymentMethodModal={handleCloseAddPaymentMethodModal}
          setOpenSnackbar={setOpenSnackbar}
          setAlertMessage={setAlertMessage}
          setSeverity={setSeverity}
          addNewPaymentMethod={true}
          childDetails={profile}
          t={t}
        />
      )}
    </>
  );
};

export default ManagePaymentMethods;
